import { useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { Button, Spinner, TextareaControl } from '@wordpress/components';
import type { CancellationReasonMode } from '../../typings/common/booking';

interface CancelBookingConfirmationProps {
	onConfirm: (reason?: string) => void;
	onCancel: () => void;
	isSubmitting?: boolean;
	error?: string | null;
	cancellationReason?: CancellationReasonMode;
}

export function CancelBookingConfirmation({
	onConfirm,
	onCancel,
	isSubmitting = false,
	error = null,
	cancellationReason = 'off',
}: CancelBookingConfirmationProps) {
	const [reason, setReason] = useState('');

	const showReason = cancellationReason !== 'off';
	const reasonRequired = cancellationReason === 'required';
	const reasonMissing = reasonRequired && reason.trim() === '';

	return (
		<div className="gpb-cancel-booking-confirmation">
			<h3>{__('Cancel Booking', 'gp-bookings')}</h3>
			<p>
				{__(
					'Are you sure you want to cancel this booking?',
					'gp-bookings'
				)}
			</p>
			{showReason && (
				<div className="gpb-cancel-booking-reason">
					<TextareaControl
						label={
							reasonRequired
								? __('Reason for cancelling', 'gp-bookings')
								: __(
										'Reason for cancelling (optional)',
										'gp-bookings'
									)
						}
						value={reason}
						onChange={setReason}
						disabled={isSubmitting}
						rows={3}
						required={reasonRequired}
					/>
				</div>
			)}
			{error && <div className="gpb-cancel-booking-error">{error}</div>}
			<div
				className="gpb-cancel-booking-actions"
				style={{ display: 'flex', gap: '8px' }}
			>
				<Button
					variant="secondary"
					onClick={onCancel}
					disabled={isSubmitting}
				>
					{__('Keep booking', 'gp-bookings')}
				</Button>
				<Button
					variant="primary"
					isDestructive
					onClick={() => onConfirm(showReason ? reason : undefined)}
					disabled={isSubmitting || reasonMissing}
				>
					{isSubmitting ? (
						<Spinner />
					) : (
						__('Cancel booking', 'gp-bookings')
					)}
				</Button>
			</div>
		</div>
	);
}
